import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useOutletContext } from "react-router-dom";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  convertToLocalTimeZone,
  handleExportCSV,
  handleExportExcel,
} from "../common/functions";
import DateRangeDataFilter from "../components/DateRangeDataFilter";
import { branchDetails } from "../api";
import Swal from "sweetalert2";

const BranchList = () => {
  const { allData, refreshData } = useOutletContext();
  const [data, setData] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    key: "",
    name: "",
    address: "",
    managedBy: "",
    contact: "",
    map: "",
    city: "",
  });
  const filename = `Branch`;

  useEffect(() => {
    if (allData?.branchDetails) {
      const parsedData = allData.branchDetails.map(value => ({
        ...value,
        created_at: convertToLocalTimeZone(value?.created_at).format(
          "YYYY-MM-DD"
        ),
      }));
      setParsedData(parsedData);
      setData(parsedData);
    }
  }, [allData]);

  const handleOpenModal = (branch = null) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({
        key: branch.key || "",
        name: branch.name || "",
        address: branch.address || "",
        managedBy: branch.managedBy || "",
        contact: branch.contact || "",
        map: branch.map || "",
        city: branch.city || "",
      });
    } else {
      setEditingBranch(null);
      setFormData({
        key: "",
        name: "",
        address: "",
        managedBy: "",
        contact: "",
        map: "",
        city: "",
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingBranch(null);
    setFormData({
      key: "",
      name: "",
      address: "",
      managedBy: "",
      contact: "",
      map: "",
      city: "",
    });
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.key.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Key is required",
        zIndex: 1500,
      });
      return false;
    }
    if (!formData.name.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Name is required",
        zIndex: 1500,
      });
      return false;
    }
    if (!formData.address.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Address is required",
        zIndex: 1500,
      });
      return false;
    }
    if (!formData.contact.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Contact is required",
        zIndex: 1500,
      });
      return false;
    }
    if (!formData.city.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "City is required",
        zIndex: 1500,
      });
      return false;
    }
    if (!formData.map.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Map is required",
        zIndex: 1500,
      });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      if (editingBranch) {
        await branchDetails.update(editingBranch.id, formData);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Branch updated successfully",
          zIndex: 1500,
        });
      } else {
        await branchDetails.create(formData);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Branch created successfully",
          zIndex: 1500,
        });
      }
      handleCloseModal();
      // Refresh data
      if (refreshData) {
        refreshData();
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Failed to save branch",
        zIndex: 1500,
      });
    }
  };

  const handleDelete = async branch => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await branchDetails.delete(branch.id);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Branch has been deleted.",
        });
        // Refresh data
        if (refreshData) {
          refreshData();
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: err?.response?.data?.message || "Failed to delete branch",
        });
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 150,
      },
      {
        accessorKey: "key",
        header: "Key",
        size: 150,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
      },
      {
        accessorKey: "address",
        header: "Address",
        size: 200,
      },
      {
        accessorKey: "managedBy",
        header: "Managed By",
        size: 150,
      },
      {
        accessorKey: "contact",
        header: "Contact",
        size: 150,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 150,
      },
      {
        accessorKey: "map",
        header: "Map",
        size: 200,
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: { density: "compact" },
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "8px" }}>
        <Button
          size='small'
          onClick={() => handleOpenModal(row.original)}
          startIcon={<EditIcon />}
        >
          Edit
        </Button>
        <Button
          size='small'
          color='error'
          onClick={() => handleDelete(row.original)}
          startIcon={<DeleteIcon />}
        >
          Delete
        </Button>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={() => handleOpenModal()}
          startIcon={<AddIcon />}
          variant='contained'
          color='primary'
        >
          Add Branch
        </Button>
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportCSV(data, filename)}
          startIcon={<FileDownloadIcon />}
        >
          Export CSV
        </Button>
        <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          onClick={() => handleExportExcel(data, filename)}
          startIcon={<FileDownloadIcon />}
        >
          Export Excel
        </Button>
      </Box>
    ),
  });

  return (
    <Box
      my={2}
      mx='5vw'
      className='about center-main main-background-image'
      sx={{ display: { xs: "flex" }, flexDirection: "column" }}
    >
      <Box className='content' m='2vw'>
        <Typography
          className='title-color'
          fontWeight={500}
          variant='h4'
          gutterBottom
        >
          Branch
        </Typography>
      </Box>
      <DateRangeDataFilter data={parsedData} setData={setData} />
      <Box width='100%'>
        <MaterialReactTable table={table} />
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          {editingBranch ? "Edit Branch" : "Add New Branch"}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              name='key'
              label='Key'
              value={formData.key}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name='name'
              label='Name'
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name='address'
              label='Address'
              value={formData.address}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
              required
            />
            <TextField
              name='managedBy'
              label='Managed By (Optional)'
              value={formData.managedBy}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              name='contact'
              label='Contact'
              value={formData.contact}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name='city'
              label='City'
              value={formData.city}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name='map'
              label='Map'
              value={formData.map}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
              required
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant='subtitle2' gutterBottom>
                Map Preview:
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "820px",
                    height: "400px",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {formData.map && formData.map.trim() ? (
                    <iframe
                      key={formData.map}
                      width='100%'
                      height='400'
                      id='map-preview'
                      src={formData.map}
                      frameBorder='0'
                      scrolling='no'
                      marginHeight='0'
                      marginWidth='0'
                      style={{
                        border: "none",
                        width: "100%",
                        height: "100%",
                      }}
                      title='Map Preview'
                    />
                  ) : (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ textAlign: "center", p: 2 }}
                    >
                      Enter a map URL above to see the preview here
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSave} variant='contained' color='primary'>
            {editingBranch ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchList;
